import { useState } from "react"
import { toast } from "react-toastify"
import { returnBook } from "../../../service/book-issue"

interface Props {
  issue: any | null
  onClose: () => void
  onSuccess?: () => void
}

const ReturnBookModal = ({ issue, onClose, onSuccess }: Props) => {
  const [returnCondition, setReturnCondition] = useState("")
  const [damageFine, setDamageFine] = useState("")
  const [loading, setLoading] = useState(false)

  if (!issue) return null

  const handleClose = () => {
    setReturnCondition("")
    setDamageFine("")
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!returnCondition) {
      toast.error("Please select book condition on return.")
      return
    }
    if (returnCondition === "damaged") {
      const percent = Number(damageFine)
      if (damageFine === "" || Number.isNaN(percent) || percent < 0 || percent > 100) {
        toast.error("Please enter a valid damage fine percentage (0–100).")
        return
      }
    }
    try {
      setLoading(true)
      const conditionRemarks =
        returnCondition === "misplaced"
          ? "Book returned via system (misplaced)"
          : returnCondition === "damaged"
            ? "Book returned via system (damaged)"
            : "Book returned via system"
      const response = await returnBook({
        issue_id: issue._id,
        return_date: new Date().toISOString().split("T")[0],
        remarks: conditionRemarks,
        fine_amount: 0,
        conditionOnReturn: returnCondition || undefined,
        damageFinePercent:
          returnCondition === "damaged" &&
          damageFine !== "" &&
          !Number.isNaN(Number(damageFine)) &&
          Number(damageFine) >= 0 &&
          Number(damageFine) <= 100
            ? Number(damageFine)
            : undefined,
      })
      if (response.success) {
        toast.success("Book returned successfully!")
        onSuccess?.()
        handleClose()
      } else {
        toast.error((response as any).message || "Failed to return book")
      }
    } catch (err: any) {
      toast.error(err.message || "Error returning book")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div
        className="modal show d-block"
        style={{ zIndex: 1060 }}
        onClick={e => { if (e.target === e.currentTarget) handleClose() }}
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Return Book</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                onClick={handleClose}
                disabled={loading}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Book Condition on Return *
                  </label>
                  <select
                    className="form-select"
                    value={returnCondition}
                    onChange={e => setReturnCondition(e.target.value)}
                    required
                  >
                    <option value="">Select condition...</option>
                    <option value="same">Same as issued / OK</option>
                    <option value="misplaced">Misplaced / Missing</option>
                    <option value="damaged">Damaged / Worse than issued</option>
                  </select>
                </div>
                {(returnCondition === "damaged" || returnCondition === "misplaced") && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {returnCondition === "misplaced" ? "Fine Percentage (%)" : "Damage Fine Percentage (%) *"}
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      max={100}
                      step={1}
                      value={returnCondition === "misplaced" ? "100" : damageFine}
                      onChange={e => returnCondition !== "misplaced" && setDamageFine(e.target.value)}
                      readOnly={returnCondition === "misplaced"}
                      placeholder="e.g. 50"
                      required={returnCondition === "damaged"}
                    />
                    {returnCondition === "misplaced" && (
                      <div className="form-text text-muted">
                        Misplaced books are charged at 100% of book price.
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? (
                    <><span className="spinner-border spinner-border-sm me-2" />Returning...</>
                  ) : (
                    "Save & Return"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1059 }} />
    </>
  )
}

export default ReturnBookModal
